export const PatientDataStoreDocumentationMarkdown = `

## Introduction
The **Smart Fetch from Chart** block lets you query a patient’s clinical data from the EHR through Notable’s Patient Data Store. You choose an endpoint (for example, Medications or Lab Values), optionally add filters (such as date range or status), and the block returns structured data you can use in subsequent steps.

For improved reliability and performance, most endpoints—excluding Booked Appointments—use a managed caching layer that reduces redundant EHR calls, mitigates rate‑limiting pressure, and delivers faster, more consistent data retrieval.

The block also provides a **Document Search** endpoint, which allows you to search the patient’s documents using a natural language query.

## When to use this block
Use this block when you need reliable read access to clinical data (encounters, conditions, medications, labs, and more) for downstream workflow logic, analytics, or automation.

## Prerequisites
- **Patient Source**: an upstream block that provides patient context (required for most endpoints).

- Optional context sources depending on the endpoint (for example, **Encounters Source** or **Provider/Practitioner Source**).

## Outputs
All endpoints return data in a consistent wrapper:
\`\`\`
  {
    "data": [
      // list of objects defined by the endpoint
    ]
  }
\`\`\`


## Caching

For improved reliability and performance, most endpoints (excluding Booked Appointments) use a managed caching layer that reduces redundant EHR calls, mitigates rate‑limiting pressure, and delivers faster, more consistent data retrieval.

The first time you request patient data, it's fetched from the EHR and cached (saved). This means faster responses — if you request the same data within 2 hours, you'll get the saved copy instantly instead of waiting for another EHR fetch.

If you request the same data after 2 hours, it's refreshed from the EHR. This saves time and reduces load because instead of re-downloading everything, only new items are added. For example, if you fetched a patient's documents 2 days ago, requesting them again will only fetch documents added in the past 2 days.

This caching works across all workflows, making your entire system more efficient. If multiple workflows need the same patient data, only the first one triggers an EHR fetch. The others use the saved copy, so you avoid duplicate work and get faster results.


## Rate Limiting & Retries

If a fetch to the Patient Data Store fails on a retryable status (\`429\`, \`408\`, or most \`5xx\`), the block automatically retries — up to **3 total attempts** (1 initial + 2 retries). Non-retryable responses surface immediately.

**Delay between retries:** exponential backoff (**10 seconds, then 20 seconds**), up to **3 total attempts**. 

**Per-attempt timeout:** each attempt has up to several minutes to complete before the block is retried.

Because most endpoints sit behind the caching layer described above, retry pressure on the underlying EHR is minimized — retries typically fire only when an EHR fetch is actually needed (a cache miss, or a refresh after the 2-hour freshness window).

## Source EHR
If an organization maintains a separate test (TST) EHR instance, practices can be configured to link to that instance. When a patient belongs to a test practice, their data is fetched from the test EHR instance instead of the production instance.

## Configure the block
1. Select a **Patient Source** that provides the patient context for this query.

2. Choose the **Patient Data Store Endpoint** (for example, “Medications,” “Encounters,” or “Lab Values”).

3. (Optional) Set endpoint‑specific filters to narrow results.

4. (Optional) Enable **Mock output for test run** while developing to validate configuration without hitting live data.

Most text fields allow using double curly braces to reference data from ancestor blocks. For example, if an ancestor block named \`patientBirthDay\` produces a birthday string, you can reference it as \`{{patientBirthDay.data}}\`.

## Configuration examples
### Example: All medications for a patient
- Patient Source: a block providing patient context

- Endpoint: Medications

- Filters: leave blank to retrieve all medications

### Example: Active medications for a specific encounter
- Patient Source: a block providing patient context

- Endpoint: Medications

- Filters:
  - Encounters Source: select an upstream encounter provider block
  - Medication Status: active

### Example: Lab values from the last 30 days
- Patient Source: a block providing patient context

- Endpoint: Lab Values

- Filters:
  - Look‑back duration: 30 days

## Advanced filters
Toggle **Advanced** to enable additional filtering for most endpoints. You can add multiple filters and set them to “match all” or “match any.”

For example, to match documents on or after November 1, 2023, use:
- Field: \`date\`
- Operator: \`greater or equal to\`
- Value: \`2023-11-01\`

You can also use nested filtering. For example:
- Field: \`rawJson.type.coding.display\`
- Operator: \`equals\`
- Value: \`Outside Documentation\`

\`\`\`
[
    {
        "id": "0195d8bc-3f52-73b9-8294-f0cacbb7a838",
        "patientId": "0190f0a1-8e15-7dbe-8aba-2c72ab406427",
        "encounterId": null,
        "type": "Outside Documentation",
        "title": "Some Test 11/3/2023",
        "date": "2023-11-03T07:00:00.000Z",
        "rawJson": {
            "resourceType": "DocumentReference",
            "id": "e2rHNDESaDwgbc-CzjxTW1ldMX1rEGOiXg9jWejvl-zA3",
            "status": "current",
            "docStatus": "final",
            "type": {
                "coding": [
                    {
                        "system": "urn:oid:1.2.840.114350.1.13.284.2.7.4.686783.100",
                        "code": "3101326392",
                        "display": "Outside Documentation"
                    }
                ],
                "text": "Outside Documentation"
            },
\`\`\`

Notes:
- Some nested fields are arrays (for example, \`rawJson.type.coding\`). A filter like \`rawJson.type.coding.display equals "Outside Documentation"\` matches if at least one element in the array satisfies the condition.

- Some endpoints include a **rawJson** object with the original FHIR payload. The **rawJson** is not shown in typical outputs. If you filter on a rawJson field (for example, \`rawJson.type.coding.display\`), the rawJson is included in the output to support evaluation and debugging. Use sufficiently narrow filters to avoid returning overly large result sets.

## Endpoints and filters
Below are commonly used endpoints and key filters and output fields.

### Practitioners
You can find a practitioner. You must provide:
- Upstream **patient** block that contains patient info
- One of the following for NPI:
  - A provider block that contains practitioner info, or
  - NPI number

### Encounters

#### Filtering
 - You can filter encounter by a look back duration. For example, if you want to get all encounters from the last 30 days, you can set the duration to "30" and unit to "days".
 - You can provide a "limit" to the number of encounters to return. If provided, N most recent encounters will be returned.

#### Output
The returned encounter will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **patientId**: string($uuid) The unique identifier for the entity in Patient Data Store.
- **practitioners**: List of practitioners involved in the encounter.
- **types**: List of types of the encounter, e.g. "Refill", "Telephone", "Office Visit".
- **reasons**: List of reasons for the encounter, e.g. "Refill Request", "Other low back pain", "Cough".
- **startTime**: string($date-time) The start time of the encounter.
- **locations**: List of the locations where the encounter happened.

### Conditions

#### Filtering
You can filter condition by:
- **status**: The status of the condition, e.g. "active", "inactive", etc.
- **condition verification status**: The verification status of the condition, e.g. "confirmed", "unconfirmed", etc.
- **condition kind**: The kind of the condition. It can be either ENCOUNTER_DIAGNOSIS or PROBLEM_LIST
- You can provide a "limit" to the number of conditions to return. If provided, N most recent conditions (by onset date)will be returned.
- You can provide an **Encounter Source** block, which should be a Smart Fetch from chart block with the **Endpoint** set to **Encounters**, to filter for conditions linked to those encounters.

#### Output
The returned condition will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **onsetDate**: string($date-time) The date a condition was noted. Format: YYYY-MM-DD
- **kind**: string The kind of the condition. Either PROBLEM_LIST or ENCOUNTER_DIAGNOSIS
- **verificationStatus**: string The verification status of the condition.
- **clinicalStatus**: string The clinical status of the condition.
- **encounterId**: string The encounter id of the condition in Patient Data Store.
- **encounter**: The encounter of the condition, may be empty.
- **problem**: string The name of the condition.
- **icd10s**: List of icd10s code of the condition.
- **encounterDate**: string($date-time) The date of the encounter of the condition. May be empty.

### Lab Values

#### Filtering
- You can filter lab value by a look back duration. For example, if you want to get all lab values from the last 30 days, you can set the duration to 30 and unit to days.
- You can provide a "limit" to the number of lab values to return. If provided, N most recent lab values (by test time) will be returned.
- You can provide a encounter source block, which should be a Smart Fetch from Chart block that fetches encounters, to filter for lab values linked to those encounters.
Note that we only return certain categories of lab values.

#### Output
The returned lab value will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **testTime**: string($date-time) The datetime the test was performed. Format: YYYY-MM-DDTHH:mm:ssZ
- **testName**: string The name of the test.
- **loinc**: string The LOINC code of the test. This is the loinc code we derived from fhir response. It may be empty.
- **value**: string The value of the test, may be empty.
- **notes**: string Any additional notes about the test, may be empty.
- **components**: List of the components of the test, this will be used if the test has multiple components.
- **referenceRanges**: List of the reference range of the test, may be empty.
- **encounter**: The encounter of the test, may be empty.

### Medications

#### Filtering
- You can filter medication by its status.
- You can provide a "limit" to the number of medications to return. If provided, N most recent medications (by prescription date) will be returned.
- You can provide a encounter source block, which should be a Smart Fetch from Chart block that fetches encounters, to filter for medications linked to those encounters.

#### Output
The returned medication will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **prescriptionDate**: string($date-time) The date the medication was prescribed. Format: YYYY-MM-DD
- **status**: string The status of the medication.
- **medicationCategory**: string The medication category of the medication.
- **encounterId**: string The encounter id of the medication in Patient Data Store.
- **encounter**: The encounter of the medication, may be empty.
- **name**: string The name of the medication.
- **details**: string The detail of the medication in FHIR.
- **dosageQuantity**: List of the dosage quantity of the medication. It will have two fields:
  - **value**: string The value of the dosage quantity.
  - **unit**: string The unit of the dosage quantity.

### Surgical Histories

#### Filtering
- You can filter surgical history by its status.
- You can provide a "limit" to the number of surgical histories to return. If provided, N most recent surgical histories (by performance datetime) will be returned.

#### Output

The returned surgical history will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **performanceDatetime**: string($date-time) The date the procedure was performed. Format: YYYY-MM-DD
- **notableName**: string The name of the procedure used by Notable.
- **nameRaw**: string The raw name of the procedure in FHIR.
- **cptCode**: string The CPT code of the procedure.
- **snomedCode**: string The SNOMED code of the procedure.
- **laterality**: string The laterality of the procedure.
- **comment**: string The comment for the procedure.
- **status**: string The status of the surgical history. Will be one of the following: preparation, in-progress, not-done, on-hold, stopped, completed, entered-in-error, unknown

### Document Metadata

Document metadata is the metadata of the document in the EHR. We return document metadata for document reference and diagnosis document in FHIR.

#### Filtering
- You can filter document metadata by a look back duration. For example, if you want to get all document metadata from the last 30 days, you can set the duration to 30 and unit to days.
- You can provide a "limit" to the number of document metadata to return. If provided, N most recent document metadata (by date) will be returned.
- You can also provide filter values for document title and type. Both filters are **contain** filters (e.g. "note" will match "note" and "any notes"), and are case insensitive. If provided, only document metadata that fulfills the filter will be returned.
- You can provide a encounter source block, which should be a Smart Fetch from Chart block that fetches encounters, to filter for documents linked to those encounters.
Please note that we will not return document metadata older than 10 years regardless of the duration you set.

#### Output
The returned document metadata will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **encounterId**: string($uuid) The encounter id of the document in Patient Data Store.
- **date**: string($date-time) The date of the document in ISO 8601 format. E.g. 2024-01-01T00:00:00Z
- **type**: string The type of the document.
- **title**: string The title of the document.
- **source**: string The source of the document. It will be one of the following: DOCUMENT_REFERENCE, DIAGNOSTIC_REPORT
- **documents**: List of the documents objects that linked to this metadata. It will contain an **id** field that can be used to fetch the document later.
- **rawJson**: string The raw JSON as returned by FHIR of the document metadata.

### Document Search

Document search enables you get relevant documents for a patient. It does this by executing a semantic search over a patient's documents and returning a ranked list of most relevant documents.

For example, given the query: "Find documents indicating that the patient has been diagnosed with major depressive disorder", the search will be able to retrieve documents with semantically similar phrases like "the patient reports persistent feelings of sadness, with loss of interest and pleasure in activities", even though the document does not contain keywords like "depression", "depressive disorder", etc. This makes document search more flexible than keyword based approaches, although keyword filters can also be specified if desired (see the section on filtering).

The search query should be a full length sentence or multiple sentences describing what documents should be retrieved. By default, the search will return the top 10 results from documents within the past year, but these defaults can be overridden by specifying them in the query.

#### Filtering
You can specify filters in the query using natural language, such as "The document title must contain the keywords 'Office Visit'", or "The document text must contain the keyword 'atherosclerosis'"
Currently, the following filter fields are supported:
- **title**
- **type**
- **text**
- **date**: Note that this range can use absolute dates ("prior to 3/15/2025", "between 2/20/2023 and 3/20/2023") or relative date expressions ("in the past 45 days", "since the start of the calendar year")

#### Output
The block returns a list of documents ranked in descending order by search relevance, where each result contains:
- **documentId**: string The id of the document.
- **text**: string The text of the document. This will be a transcript if the original document is an image, PDF, or other non-text format
- **metadata**: object The metadata object of the document. See the document metadata section for more details.

#### Behavior
When the document search executes, it will need to download and process any documents that are not already in the Patient Data Store before it can search them. This means that the first search for a patient will be much slower than subsequent searches, since for subsequent searches, most or all of the documents will be already present in the data store and will not need to be redownloaded.

A good way to speed up the initial document search is to specify metadata filters that exclude irrelevant documents. For example, if you know that the documents you want to retrieve will have "Cancer Screening" in the document title, you can exclude a potentially large number of irrelevant documents and avoid having to wait to download them.

These are the steps that are executed for document search:
1. The patient's document metadata is fetched. This can take up to a few minutes
2. Document metadata that match the filters (title and type) are downloaded and transcribed. This can take up to a minute per document
3. The document transcripts are processed and indexed to make them searchable
4. The documents are searched, and a list of documents ranked by search relevance is returned

### Document Transcriptions
You can request transcription of documents.

Make sure to set the **Document metadata source** to the block that filters document metadata.
The **Document metadata source** block should output a list of **document metadata**.
It is possible that one document metadata object contains multiple documents. All documents in the document metadata object will be transcribed.

IMPORTANT: The transcription process is expensive and time consuming. Make sure to filter the document metadata before feeding it to this block.
The maximum number of documents to transcribe is 100.

The returned document transcription will have the following fields:
- **documentId**: string The id of the document.
- **text**: string The transcription of the document. A document may failed to transcribe. If that happens, the text will be empty. And the error will be set.
- **error**: string The error of the transcription, if any. This will be set if text is empty.
- **metadata**: object The metadata object of the document. See the document metadata section for more details.

### Orders

This endpoint will return orders for a patient.

#### Filtering
You can filter orders by:
- You can provide a encounter source block, which should be a Smart Fetch from Chart block that fetches encounters, to filter for documents linked to those encounters.
- You can provide a provider (practitioner) source block to filter for orders by that provider. The source block's data must contain a 'npi' field.
- You can provide a status filter. Must be one of "active", "completed", "revoked"
- You can provide a "limit" to the number of orders to return. If provided, N most recent orders (by created at) will be returned.

#### Output

The returned order will have the following fields:
- **id**: string($uuid) The unique identifier for the object in the Patient Data Store.
- **externalId**: string The unique identifier for the object in the EHR.
- **patientId**: string($uuid) The unique identifier for the entity in Patient Data Store.
- **occurredAt**: string($date-time) The datetime the order occurred.
- **createdAt**: string($date-time) The datetime the order was created by the practitioner
- **encounterId**: string($uuid) The encounter ID associated with this order. This is the encounter id in Patient Data Store.
- **practitionerId**: string($uuid) The practitioner ID associated with this order. This is the practitioner id in Patient Data Store.
- **status**: string The status of the order. Must be one of "active", "completed", "revoked"
- **rawJson**: object The raw JSON as returned by FHIR of the order.

### Booked Appointments

This endpoint will return booked appointments for a patient.

#### Filtering
You can filter booked appointments by:
- The appointment date for appointments happens N days from now.
- You can provide a "limit" to the number of booked appointments to return. If provided, N earliest booked appointments (by start time) will be returned.

#### Output

The returned booked appointment will have the following fields:
- **slotId**: string The slot ID of the booked appointment.
- **encounterIdentifier**: string The encounter identifier of the booked appointment.
- **departmentId**: string The department ID of the booked appointment.
- **comment**: string The comment for the booked appointment.
- **startTime**: string($date-time) The start time of the booked appointment.
- **endTime**: string($date-time) The end time of the booked appointment.
- **practiceId**: string($uuid) The practice ID of the booked appointment.
- **providerId**: string($uuid) The provider ID of the booked appointment.
- **providerName**: string The provider name of the booked appointment.
- **appointmentKind**: string The kind of the booked appointment.
- **appointmentKindRaw**: string The raw kind of the booked appointment.
- **location**: string The location of the booked appointment.
- **caseType**: string The case type of the booked appointment.
- **vipCode**: string The VIP code of the booked appointment.
`;
